export enum CandidateRegion {
  LatAm = 'LatAm',
  Europe = 'Europe',
  USA = 'USA',
  India = 'India',
  Other = 'Other',
}

export const CandidateRegionOrder = [
  CandidateRegion.LatAm,
  CandidateRegion.Europe,
  CandidateRegion.USA,
  CandidateRegion.India,
  CandidateRegion.Other,
];

// Zoho records most US candidates per state, as '<state>, USA'.
const US_STATE_SUFFIX = ', USA';

/**
 * Keyed on `candidate.country`, which Launchpod passes through verbatim from
 * Zoho's `Location_Country`. That field is a picklist in the CRM but arrives
 * here as free text, so every country of a region is listed rather than only
 * the picklist's own values. Countries that have no region of their own are
 * listed explicitly against `Other` so a deliberately parked country reads
 * differently from one nobody has looked at yet.
 */
const RegionByCountry: Record<string, CandidateRegion> = {
  Argentina: CandidateRegion.LatAm,
  Bolivia: CandidateRegion.LatAm,
  Brazil: CandidateRegion.LatAm,
  Chile: CandidateRegion.LatAm,
  Colombia: CandidateRegion.LatAm,
  'Costa Rica': CandidateRegion.LatAm,
  Cuba: CandidateRegion.LatAm,
  'Dominican Republic': CandidateRegion.LatAm,
  Ecuador: CandidateRegion.LatAm,
  'El Salvador': CandidateRegion.LatAm,
  Guatemala: CandidateRegion.LatAm,
  Honduras: CandidateRegion.LatAm,
  Mexico: CandidateRegion.LatAm,
  Nicaragua: CandidateRegion.LatAm,
  Panama: CandidateRegion.LatAm,
  Paraguay: CandidateRegion.LatAm,
  Peru: CandidateRegion.LatAm,
  Uruguay: CandidateRegion.LatAm,
  Venezuela: CandidateRegion.LatAm,
  LatAm: CandidateRegion.LatAm,

  Albania: CandidateRegion.Europe,
  Andorra: CandidateRegion.Europe,
  Armenia: CandidateRegion.Europe,
  Austria: CandidateRegion.Europe,
  Belgium: CandidateRegion.Europe,
  'Bosnia and Herzegovina': CandidateRegion.Europe,
  Bulgaria: CandidateRegion.Europe,
  Croatia: CandidateRegion.Europe,
  Cyprus: CandidateRegion.Europe,
  'Czech Republic': CandidateRegion.Europe,
  Czechia: CandidateRegion.Europe,
  Denmark: CandidateRegion.Europe,
  Estonia: CandidateRegion.Europe,
  Finland: CandidateRegion.Europe,
  France: CandidateRegion.Europe,
  Georgia: CandidateRegion.Europe, // the country; the US state is 'Georgia, USA'
  Germany: CandidateRegion.Europe,
  Greece: CandidateRegion.Europe,
  Hungary: CandidateRegion.Europe,
  Iceland: CandidateRegion.Europe,
  Ireland: CandidateRegion.Europe,
  Italy: CandidateRegion.Europe,
  Kosovo: CandidateRegion.Europe,
  Latvia: CandidateRegion.Europe,
  Liechtenstein: CandidateRegion.Europe,
  Lithuania: CandidateRegion.Europe,
  Luxembourg: CandidateRegion.Europe,
  Malta: CandidateRegion.Europe,
  Moldova: CandidateRegion.Europe,
  Monaco: CandidateRegion.Europe,
  Montenegro: CandidateRegion.Europe,
  Netherlands: CandidateRegion.Europe,
  'North Macedonia': CandidateRegion.Europe,
  Norway: CandidateRegion.Europe,
  Poland: CandidateRegion.Europe,
  Portugal: CandidateRegion.Europe,
  Romania: CandidateRegion.Europe,
  'San Marino': CandidateRegion.Europe,
  Serbia: CandidateRegion.Europe,
  Slovakia: CandidateRegion.Europe,
  Slovenia: CandidateRegion.Europe,
  Spain: CandidateRegion.Europe,
  Sweden: CandidateRegion.Europe,
  Switzerland: CandidateRegion.Europe,
  Ukraine: CandidateRegion.Europe,
  'United Kingdom': CandidateRegion.Europe,
  Europe: CandidateRegion.Europe,

  USA: CandidateRegion.USA,
  'United States': CandidateRegion.USA,
  'United States of America': CandidateRegion.USA,

  India: CandidateRegion.India,

  // Known picklist values with no region of their own. Africa and Asia are not
  // regions the filter offers, so these land in `Other` on purpose.
  Africa: CandidateRegion.Other,
  Canada: CandidateRegion.Other,
  Egypt: CandidateRegion.Other,
  Kazakhstan: CandidateRegion.Other,
  Kenya: CandidateRegion.Other,
  Russia: CandidateRegion.Other,
  Uzbekistan: CandidateRegion.Other,
  Other: CandidateRegion.Other,
};

export const getCandidateRegion = (
  country?: string | null,
): CandidateRegion => {
  if (!country) return CandidateRegion.Other;

  const name = country.trim();
  if (name.endsWith(US_STATE_SUFFIX)) return CandidateRegion.USA;

  return RegionByCountry[name] ?? CandidateRegion.Other;
};
